import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useUserNotifications, NOTIFICATIONS_LIST_KEY } from '@/notifications/services/use-user-notifications';
import { useMarkNotificationsAsRead } from '@/notifications/services/use-mark-notifications-as-read';
import { useResetNotificationSeenCount } from '@/notifications/services/use-reset-notification-seen-count';
import { useNotificationPanelStore } from '@/notifications/store/notification-panel-store';
import { dedupeAndSortNotifications } from '@/notifications/lib/dedupe-sort-notifications';
import { NotificationRow } from './notification-row';
import { NotificationEmptyState } from './notification-empty-state';

/**
 * Notification Center side-panel — Flutter parity with
 * `notification_center_screen.dart` (the in-app Notifications popup,
 * not the standalone screen with `showAppBar=true`).
 *
 * Lifecycle (mirrors Flutter initState + dispose):
 *
 *   open:
 *     1. Fetch page 1 of UserNotifications (useUserNotifications fires
 *        on mount; the store opens once the user clicks the Bell).
 *     2. Fire ResetNotificationSeenCount → badge drops to 0 optimistically.
 *
 *   while open:
 *     3. Each row that becomes visible registers via IntersectionObserver:
 *        - Within last 5 rows: trigger fetchNextPage. The flattened pages are
 *          then deduped-by-id + sorted-desc via dedupeAndSortNotifications
 *          (Flutter bloc:90-97) — React Query does NOT dedupe across pages.
 *        - If unread: add id to `seenDuringOpen` Set (store action).
 *
 *   close:
 *     4. If seenDuringOpen.size > 0: fire MarkNotificationsAsRead with
 *        Array.from(set). React closes immediately (Flutter waits for
 *        the response then `Navigator.pop(true)` — we don't block the UI
 *        on a fire-and-forget mutation).
 *     5. Reset the store's seen Set.
 *     6. Invalidate `['notifications', 'list']` so next open re-fetches
 *        from page 1 with fresh isRead state.
 *
 * Per-row tap navigates to `/scheduling/appointment/:appointmentId`
 * when `notification.data.appointmentId` is set. Otherwise the tap is a
 * no-op (Flutter calls `widget.onItemClick(...)` with empty string when
 * appointmentId is null — equivalent here is "do nothing").
 */
export function NotificationPanel() {
  const open = useNotificationPanelStore((s) => s.open);
  const closePanel = useNotificationPanelStore((s) => s.closePanel);
  const resetSeen = useNotificationPanelStore((s) => s.resetSeen);
  // Read the Set synchronously when closing — useStore selector below would
  // re-render the panel on every visibility tick, which is wasteful.
  const getSeenDuringOpen = () => useNotificationPanelStore.getState().seenDuringOpen;

  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const userNotifications = useUserNotifications();
  const markAsRead = useMarkNotificationsAsRead();
  const resetSeenCount = useResetNotificationSeenCount();

  // Only fire ResetNotificationSeenCount once per open. Without this guard,
  // the effect would re-fire on `resetSeenCount` reference identity churn.
  const seenResetFiredForThisOpen = useRef(false);
  useEffect(() => {
    if (open && !seenResetFiredForThisOpen.current) {
      seenResetFiredForThisOpen.current = true;
      resetSeenCount.mutate();
    }
    if (!open) {
      seenResetFiredForThisOpen.current = false;
    }
  }, [open, resetSeenCount]);

  const handleOpenChange = (next: boolean) => {
    if (next) return; // opening is store-driven via Bell click
    const seen = getSeenDuringOpen();
    if (seen.size > 0) {
      markAsRead.mutate({ notificationIds: Array.from(seen) });
    }
    resetSeen();
    closePanel();
    // Drop the list cache so next open re-fetches with fresh isRead flags.
    void qc.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_KEY });
  };

  // Flutter bloc:90-97 dedupes by id + sorts desc by createdOn after every
  // page. React Query does NOT dedupe across pages, so a notification arriving
  // mid-session can duplicate a row (→ colliding React keys). Restore the
  // invariant here. See dedupe-sort-notifications.ts.
  const allRows = dedupeAndSortNotifications(
    userNotifications.data?.pages.flat() ?? [],
  );
  const totalCount = allRows.length;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="!w-[var(--notif-panel-width)] !max-w-[var(--notif-panel-width)] p-0 flex flex-col">
        <SheetHeader className="border-b border-[var(--surface-line)] px-4 py-3">
          <SheetTitle className="[font:var(--type-card-title)] text-[var(--ink-body)]">
            Notifications
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto scrollbar-hide bg-[var(--color-surface-background)] px-2 py-2">
          {totalCount === 0 && !userNotifications.isLoading ? (
            <NotificationEmptyState />
          ) : (
            <div className="flex flex-col gap-2">
              {allRows.map((notification, index) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  index={index}
                  totalCount={totalCount}
                  onVisibleNearEnd={() => {
                    if (
                      userNotifications.hasNextPage &&
                      !userNotifications.isFetchingNextPage
                    ) {
                      void userNotifications.fetchNextPage();
                    }
                  }}
                  onClick={() => {
                    const apptId = notification.data?.appointmentId;
                    if (!apptId) return;
                    handleOpenChange(false);
                    navigate(
                      `/scheduling/appointment/${apptId}?from=${encodeURIComponent(location.pathname + location.search)}`,
                    );
                  }}
                />
              ))}
              {userNotifications.isFetchingNextPage && (
                <div className="py-3 text-center [font:var(--type-body)] text-[var(--ink-meta)]">
                  Loading more…
                </div>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
